import { queryClient } from '$/services/client';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import { validateToken } from '$/services/usecases/authentication';
import {
  GetAllSchoolClassesResponseSchema,
  GetSchoolClassByIdResponseSchema,
  GetSchoolClassProjectsResponseSchema,
  UpsertSchoolClassResponseSchema,
  type SchoolClass,
} from '$/services/usecases/school/mapper/schoolClasses';
import { schoolClassesQuery } from '$/services/usecases/school/queries';

export const getSchoolClassById = async (signal: AbortSignal, id?: string) => {
  if (id == null) {
    return {
      isSuccessful: true,
      httpStatusCode: 200,
      response: null,
    };
  }

  await validateToken();
  return fetcher(`${backendEndpoints.SCHOOLCLASSES_URL}/${id}`, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetSchoolClassByIdResponseSchema.parse(data).payload,
    signal,
  });
};

export const getAllSchoolClasses = async (signal: AbortSignal) => {
  await validateToken();
  return fetcher(backendEndpoints.SCHOOLCLASSES_URL, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetAllSchoolClassesResponseSchema.parse(data).payload,
    signal,
  });
};

export const createSchoolClass = async (
  schoolClass: Omit<SchoolClass, 'id'>,
) => {
  await validateToken();

  const response = await fetcher(backendEndpoints.SCHOOLCLASSES_URL, {
    method: 'POST',
    includeCredentials: true,
    body: schoolClass,
    parser: (data) => UpsertSchoolClassResponseSchema.parse(data).payload,
  });

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: schoolClassesQuery().queryKey,
    });
  }

  return response;
};

export const updateSchoolClass = async (schoolClass: SchoolClass) => {
  await validateToken();

  const response = await fetcher(
    `${backendEndpoints.SCHOOLCLASSES_URL}/${schoolClass.id}`,
    {
      method: 'PATCH',
      includeCredentials: true,
      body: schoolClass,
      parser: (data) => UpsertSchoolClassResponseSchema.parse(data).payload,
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: schoolClassesQuery().queryKey,
    });
  }

  return response;
};

export const getSchoolClassProjects = async (
  signal: AbortSignal,
  schoolClassId: string,
) => {
  await validateToken();
  return fetcher(
    `${backendEndpoints.SCHOOLCLASSES_URL}/${schoolClassId}/projects`,
    {
      method: 'GET',
      includeCredentials: true,
      parser: (data) =>
        GetSchoolClassProjectsResponseSchema.parse(data).payload,
      signal,
    },
  );
};

export const deleteSchoolClass = async (schoolClass: SchoolClass) => {
  await validateToken();

  const response = await fetcher(
    `${backendEndpoints.SCHOOLCLASSES_URL}/${schoolClass.id}`,
    {
      method: 'DELETE',
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: schoolClassesQuery().queryKey,
    });
  }

  return response;
};

export const shareProjectWithClass = async (payload: {
  schoolClassIds: string[];
  projectId: string;
}) => {
  await validateToken();
  return fetcher(`${backendEndpoints.SCHOOLCLASSES_URL}/share`, {
    method: 'POST',
    includeCredentials: true,
    body: payload,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};
